<template>
  <div class="role-list">
    <header class="page-header">
      <button class="back" @click="$router.back()"><van-icon name="arrow-left"/></button>
      <div class="page-title">角色管理</div>
      <div class="header-right">
        <button class="add-btn" @click="gotoEdit(null)">新增</button>
      </div>
    </header>

    <main class="content">
      <section v-if="loading" class="loading"><van-loading/></section>

      <section v-else class="role-cards">
        <div v-for="role in roles" :key="role.id" class="role-card" @click="gotoEdit(role.id)">
          <div class="card-head">
            <b>{{ role.name }}</b>
            <span class="tag" :class="'tag-' + role.dataScope">{{ scopeLabel(role.dataScope) }}</span>
            <span v-if="role.status === 'DISABLED'" class="tag tag-disabled">已禁用</span>
          </div>
          <div class="stats">
            <span>{{ role.permissionCount || (role.permissions && role.permissions.length) || 0 }} 项权限</span>
            <span class="link-like" @click.stop="gotoMembers(role)">
              {{ role.memberCount || 0 }} 名成员 <van-icon name="arrow"/>
            </span>
          </div>
          <div class="card-actions">
            <button class="action-btn members" @click.stop="gotoMembers(role)">
              <van-icon name="friends-o"/> 成员
            </button>
            <button class="action-btn edit" @click.stop="gotoEdit(role.id)">
              <van-icon name="edit"/> 编辑
            </button>
            <button class="action-btn del" @click.stop="confirmDelete(role)"
                    :disabled="role.memberCount > 0">
              <van-icon name="cross"/> 删除
            </button>
          </div>
        </div>
      </section>

      <section v-else-if="!roles.length" class="empty">
        <van-empty description="暂无角色，点击右上角新增"/>
      </section>
    </main>

    <van-dialog v-model="showDelete" title="删除角色" :message="deleteMessage"
               show-cancel-button confirm-button-text="删除"
               :confirm-button-color="'#f55'"
               @confirm="doDelete"/>
  </div>
</template>

<script>
import identityApi from '@/api/identity';

export default {
  name: 'BossRoleList',
  data() {
    return { roles: [], loading: true, showDelete: false, toDelete: null };
  },
  computed: {
    deleteMessage() {
      if (!this.toDelete) return '';
      const m = this.toDelete.memberCount || 0;
      if (m > 0) return `该角色还有 ${m} 名成员，无法删除。请先在「成员管理」里移除所有成员。`;
      return `确认删除角色「${this.toDelete.name}」？删除后不可恢复。`;
    }
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.roles = await identityApi.listRoles();
      } catch (e) {
        this.$toast.fail(e.message || '加载角色失败');
      } finally {
        this.loading = false;
      }
    },
    scopeLabel(s) {
      return {
        TENANT: '全体员工',
        DEPARTMENT: '本部门',
        DEPARTMENT_AND_CHILDREN: '部门及下级',
        SELF: '仅自己'
      }[s] || s;
    },
    gotoEdit(id) {
      if (id) this.$router.push(`/boss/identity/roles/${id}`);
      else this.$router.push('/boss/identity/roles/new');
    },
    gotoMembers(role) {
      this.$router.push({ path: '/boss/identity/employees', query: { roleId: role.id, roleName: role.name } });
    },
    confirmDelete(role) {
      this.toDelete = role;
      this.showDelete = true;
    },
    async doDelete() {
      if (!this.toDelete) return;
      const role = this.toDelete;
      this.showDelete = false;
      this.toDelete = null;
      try {
        await identityApi.deleteRole(role.id);
        this.$toast.success('角色已删除');
        await this.load();
      } catch (e) {
        this.$toast.fail(e.message || '删除失败');
      }
    }
  }
};
</script>

<style scoped>
.page-header {
  position: sticky; top: 0; z-index: 10;
  height: 44px; background: #fff;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 12px; border-bottom: 1px solid #eee;
}
.back, .add-btn, .perm-catalog-btn { background: none; border: 0; font-size: 14px; }
.add-btn { color: #1989fa; font-weight: 500; margin-left: 10px; }
.perm-catalog-btn { color: #666; font-size: 13px; }
.page-title { font-weight: 500; }
.header-right { display: flex; align-items: center; }
/* 底部 padding 需覆盖固定 Tabbar 高度（50px），否则最后一张卡片永远被遮住滚不出来 */
.content { padding: 12px 12px calc(66px + env(safe-area-inset-bottom)); }
.role-cards { display: flex; flex-direction: column; gap: 10px; }
.role-card {
  background: #fff; border-radius: 10px; padding: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,.05);
}
.card-head { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.card-head b { font-size: 16px; }
.tag {
  font-size: 11px; padding: 2px 8px; border-radius: 10px;
  background: #eef4ff; color: #3370ff;
}
.tag-TENANT { background: #fff1f0; color: #f55; }
.tag-DEPARTMENT_AND_CHILDREN { background: #fff7e6; color: #fa8c16; }
.tag-SELF { background: #f6ffed; color: #52c41a; }
.tag-disabled { background: #f5f5f5; color: #999; }
.stats {
  display: flex; gap: 14px; color: #666; font-size: 12px; margin-top: 6px; cursor: pointer;
}
.link-like { color: #1989fa; }
.card-actions {
  display: flex; gap: 6px; margin-top: 10px; padding-top: 10px;
  border-top: 1px solid #f5f5f5;
}
.action-btn {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 3px;
  height: 32px; border: 1px solid #eee; border-radius: 6px;
  background: #fafafa; font-size: 12px; color: #333; min-width: 0;
}
.action-btn.members { color: #722ed1; border-color: #722ed1; background: #f9f0ff; }
.action-btn.edit { color: #1989fa; border-color: #1989fa; background: #e8f4ff; }
.action-btn.del { color: #f55; border-color: #f55; background: #fff1f0; }
.action-btn.del[disabled] { opacity: 0.4; cursor: not-allowed; }
.loading, .empty { padding: 40px 0; text-align: center; }
</style>



// WEBPACK FOOTER //
// src/views/boss/identity/RoleList.vue